import { useEffect, useRef } from "react";

const COLORS = ["#f59e0b", "#ea580c", "#fbbf24", "#dc2626", "#fdba74"];

export const EmberCanvas = ({ density = 1, fixed = false, testid = "ember-canvas" }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

        const ctx = canvas.getContext("2d");
        let raf;
        let w = 0;
        let h = 0;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
        let particles = [];

        const resize = () => {
            if (fixed) {
                w = window.innerWidth;
                h = window.innerHeight;
            } else {
                const rect = canvas.parentElement.getBoundingClientRect();
                w = rect.width;
                h = rect.height;
            }
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const count = Math.round(Math.min(110, (w * h) / 16000) * density);
            particles = Array.from({ length: count }, () => spawn(true));
        };

        const spawn = (initial = false) => ({
            x: (Math.random() - 0.5) * 2,
            y: initial ? (Math.random() - 0.5) * 2 : 1.1 + Math.random() * 0.3,
            z: 0.15 + Math.random() * 0.85,
            size: 1 + Math.random() * 2.4,
            speed: 0.0006 + Math.random() * 0.0016,
            sway: Math.random() * Math.PI * 2,
            swaySpeed: 0.004 + Math.random() * 0.01,
            color: COLORS[(Math.random() * COLORS.length) | 0],
            alpha: 0.25 + Math.random() * 0.55,
        });

        const onMouseMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            if (rect.width === 0) return;
            mouse.tx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
            mouse.ty = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
        };

        const FOV = 320;
        const tick = () => {
            ctx.clearRect(0, 0, w, h);
            mouse.x += (mouse.tx - mouse.x) * 0.05;
            mouse.y += (mouse.ty - mouse.y) * 0.05;
            const rotY = mouse.x * 0.35;
            const rotX = mouse.y * 0.22;
            const cosY = Math.cos(rotY);
            const sinY = Math.sin(rotY);
            const cosX = Math.cos(rotX);
            const sinX = Math.sin(rotX);
            const cx = w / 2;
            const cy = h / 2;

            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];
                p.y -= p.speed;
                p.sway += p.swaySpeed;
                if (p.y < -1.2) particles[i] = spawn();

                const px = p.x + Math.sin(p.sway) * 0.06;
                const py = p.y;

                const x1 = px * cosY - p.z * sinY;
                const z1 = px * sinY + p.z * cosY;
                const y1 = py * cosX - z1 * sinX;
                const z2 = py * sinX + z1 * cosX;

                const scale = FOV / (FOV + z2 * 420);
                if (scale <= 0) continue;
                const sx = cx + x1 * cx * scale;
                const sy = cy + y1 * cy * scale;
                const r = p.size * scale;
                const a = p.alpha * Math.min(1, scale) * (0.75 + 0.25 * Math.sin(p.sway * 2));

                ctx.beginPath();
                ctx.arc(sx, sy, r, 0, Math.PI * 2);
                ctx.fillStyle = p.color;
                ctx.globalAlpha = a;
                ctx.shadowColor = p.color;
                ctx.shadowBlur = 12 * scale;
                ctx.fill();
            }
            ctx.globalAlpha = 1;
            ctx.shadowBlur = 0;
            raf = requestAnimationFrame(tick);
        };

        resize();
        tick();
        window.addEventListener("resize", resize);
        window.addEventListener("mousemove", onMouseMove, { passive: true });
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
            window.removeEventListener("mousemove", onMouseMove);
        };
    }, [density, fixed]);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            data-testid={testid}
            className={`pointer-events-none mix-blend-screen ${
                fixed ? "fixed inset-0 z-0" : "absolute inset-0"
            }`}
        />
    );
};
